import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';

import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent, type HeaderMode } from './components/header/header.component';
import {
  MainTab,
  ProductTab,
  ProductUpdatesStateService,
} from './services/product-updates-state.service';
import { ThemeService } from './services/theme.service';

type StickyState = 'hidden' | 'visible' | 'hiding';

@Component({
  selector: 'pu-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  host: {
    '(window:scroll)': 'onWindowScroll()',
  },
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
})
export class AppComponent {
  // Inject ThemeService to ensure it initializes on app bootstrap
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);

  public readonly stateService = inject(ProductUpdatesStateService);

  public readonly stickyState = signal<StickyState>('hidden');

  // Determine header mode based on whether we're on an article page
  // Use isArticlePage flag (set immediately) rather than currentArticle (set after data loads)
  public readonly headerMode = computed<HeaderMode>(() =>
    this.stateService.isArticlePage() ? 'article' : 'feed',
  );

  // Check if we're on the timeline page (fullscreen presentation mode)
  public readonly isTimelinePage = computed(() => this.stateService.activeMainTab() === 'timeline');

  private lastScrollY = 0;
  private scrollThreshold = 260; // Hide sticky header when within purple gradient area
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  public onWindowScroll(): void {
    const currentScrollY = window.scrollY;
    const currentState = this.stickyState();

    // Show sticky header when scrolling up and past threshold
    if (currentScrollY < this.lastScrollY && currentScrollY > this.scrollThreshold) {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      this.stickyState.set('visible');
    } else if (currentScrollY > this.lastScrollY || currentScrollY <= this.scrollThreshold) {
      if (currentState === 'visible') {
        this.stickyState.set('hiding');
        this.hideTimeout = setTimeout(() => {
          this.stickyState.set('hidden');
          this.hideTimeout = null;
        }, 300); // Match CSS transition duration
      }
    }

    this.lastScrollY = currentScrollY;
  }

  public onMainTabChange(tab: MainTab): void {
    // Navigate to the appropriate route, preserving query params
    const path = this.getPathForMainTab(tab);
    const currentProductTab = this.stateService.activeProductTab();
    const queryParams = currentProductTab !== 'all' ? { product: currentProductTab } : {};

    this.router.navigate([path], { queryParams });
  }

  public onProductTabChange(tab: ProductTab): void {
    // Update query params while staying on the current route
    const queryParams = tab !== 'all' ? { product: tab } : {};
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: tab === 'all' ? '' : 'merge',
    });
  }

  private getPathForMainTab(tab: MainTab): string {
    switch (tab) {
      case 'coming-soon':
        return '/coming-soon';
      case 'changelog':
        return '/changelog';
      case 'timeline':
        return '/timeline';
      case 'whats-new':
      default:
        return '/';
    }
  }
}
